import { useParams, useNavigate, Link } from "react-router-dom";
import { Form, Input, Button, App, Card, Typography } from "antd";
import { useState, useEffect } from "react";
import API from "../../../services/api";
import "./ResetPassword.css";

const { Title, Text } = Typography;

function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const handleSubmit = async () => {
    message.destroy();

    // Check internet connection first
    if (!navigator.onLine) {
      message.error("You are offline. Check your internet connection.");
      return;
    }

    // Get form values
    const values = form.getFieldsValue();

    // Check if password is empty
    if (!values.password) {
      message.error("Please enter your new password");
      return;
    }

    // Validate password length
    if (values.password.length < 6) {
      message.error("Password must be at least 6 characters");
      return;
    }

    // If validation passes, submit the form
    try {
      setLoading(true);

      await API.post(`/users/reset-password/${token}`, { password: values.password });

      message.success("Password reset successful");

      // Navigate to login page after 2 seconds
      setTimeout(() => {
        navigate("/login");
      }, 2000);

    } catch (err) {
      message.destroy();

      // Handle offline error during API call
      if (!navigator.onLine) {
        message.error("You are offline. Check your internet connection.");
      } else {
        message.error("Link already expired");
      }
    } finally {
      setLoading(false);
    }
  };

  // Check internet status when component mounts
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <div className="reset-password-page">
      <div className="reset-password-container">
        <Card className="reset-password-card" variant="borderless">
          <div className="reset-password-header">
            <div className="brand-section">
              <div className="brand-logo">D</div>
              <Title level={3} className="brand-name">Draptor</Title>
            </div>
            <Title level={4} className="reset-password-title">
              Reset password
            </Title>
            <Text className="reset-password-subtitle">
              Enter your new password below
            </Text>
          </div>

          <Form
            form={form}
            layout="vertical"
            validateTrigger={[]}
            className="reset-password-form"
            requiredMark={false}
          >
            <Form.Item
              label="New password"
              name="password"
              className="form-item"
            >
              <Input.Password
                placeholder="Enter new password"
                className="input-field"
                disabled={!isOnline}
              />
            </Form.Item>

            <Form.Item className="form-item">
              <Button
                type="primary"
                htmlType="button"
                block
                className="reset-password-btn"
                loading={loading}
                onClick={handleSubmit}
                disabled={!isOnline}
              >
                Reset password
              </Button>
            </Form.Item>

            {!isOnline && (
              <div className="offline-warning">
                <Text className="offline-text">
                  You are currently offline. Please check your internet connection.
                </Text>
              </div>
            )}

            <div className="back-to-login">
              <Text className="back-text">
                <Link to="/login">Back to login</Link>
              </Text>
            </div>
          </Form>
        </Card>
      </div>
    </div>
  );
}

export default ResetPassword;